import React, {
  useState,
  useCallback,
  useMemo,
  ChangeEvent,
  useEffect,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { BaseModal } from '../../../../components/BaseModal';
import { Input } from '../../../../components/FormComponents';
import { useNotification } from '../../../../contexts/NotificationContext';
import { useDebounce } from '../../../../hooks';
import { REMOVE_EMAIL_CONSTANTS } from '../../constants/removeEmailConstants';
import { useGetChatCountByCCEmail } from '../../hooks/useGetChatCountByCCEmail';
import { useRemoveEmailMutation } from '../../hooks/useRemoveEmailMutation';
import type {
  EmailRemoverFormData,
  RemoveEmailFormProps,
} from '../../types/removeEmail';
import { validateEmail } from '../../utils/emailValidation';
import { validateEmailForm } from '../../utils/formHelpers';
import { validationResolver } from '../../utils/removeEmailValidation';

const defaultRemoveEmailFormValues: EmailRemoverFormData = {
  additionalEmail: '',
};

const getRemoveEmailButtonLabel = (
  numberOfChats?: number | null,
  email: string = ''
) => {
  if (email !== '' && validateEmail(email)) {
    if (numberOfChats === undefined || numberOfChats === null) {
      return REMOVE_EMAIL_CONSTANTS.BUTTON_CHECKING_IN_PROGRESS_TEXT;
    }
    if (numberOfChats > 0) {
      return REMOVE_EMAIL_CONSTANTS.BUTTON_NO_OF_CHATS_TEXT(numberOfChats);
    }
    if (numberOfChats === 0) {
      return REMOVE_EMAIL_CONSTANTS.BUTTON_EMAIL_NOT_AVAILABLE_TEXT;
    }
  }

  return REMOVE_EMAIL_CONSTANTS.BUTTON_DISABLE_TEXT;
};

interface UseRemoveEmailFormProps {
  onEmailRemove?: () => void;
  closeEmailRemoveModal: () => void;
}

const useRemoveEmailForm = ({
  closeEmailRemoveModal,
}: UseRemoveEmailFormProps) => {
  const methods = useForm({
    resolver: validationResolver,
    defaultValues: defaultRemoveEmailFormValues,
  });
  const { removeEmail, loading } = useRemoveEmailMutation();
  const { addNotification } = useNotification();

  const { formState, handleSubmit, getValues } = methods;
  const additionalEmail = getValues('additionalEmail');
  const isDisabled = validateEmailForm(
    loading,
    formState.errors,
    formState.isDirty,
    additionalEmail
  );

  const onSubmit = useMemo(
    () =>
      handleSubmit(async (formData) => {
        try {
          await removeEmail({
            variables: { email: formData.additionalEmail },
          });

          addNotification({
            title: REMOVE_EMAIL_CONSTANTS.MESSAGE_HEADER,
            message: REMOVE_EMAIL_CONSTANTS.MESSAGE_SUCCESS_MESSAGE,
            type: 'success',
          });
          closeEmailRemoveModal();
        } catch (error) {
          addNotification({
            title: REMOVE_EMAIL_CONSTANTS.MESSAGE_HEADER,
            message: REMOVE_EMAIL_CONSTANTS.MESSAGE_FAILURE_MESSAGE,
            type: 'error',
          });
          if (error instanceof Error) {
            // Error already handled by notification system
          }
        }
      }),
    [handleSubmit, removeEmail, addNotification, closeEmailRemoveModal]
  );

  return {
    methods,
    isDisabled,
    onSubmit,
  };
};

export const RemoveEmailModal: React.FC<RemoveEmailFormProps> = ({
  isOpen,
  toggle,
  onEmailRemove,
}) => {
  const form = useRemoveEmailForm({
    onEmailRemove,
    closeEmailRemoveModal: toggle,
  });
  const { getChatCountByCCEmail, numberOfChats } = useGetChatCountByCCEmail();

  const [emailInput, setEmailInput] = useState('');
  const debouncedEmailInput = useDebounce(emailInput, 2000);

  // Effect to trigger chat count check when debounced email changes
  useEffect(() => {
    const checkChatCount = async () => {
      if (debouncedEmailInput && validateEmail(debouncedEmailInput)) {
        try {
          await getChatCountByCCEmail({
            variables: { email: debouncedEmailInput },
          });
        } catch (error) {
          if (error instanceof Error) {
            // Error handled by hook internally
          }
        }
      }
    };

    checkChatCount();
  }, [debouncedEmailInput, getChatCountByCCEmail]);

  const isReadyToSubmit =
    numberOfChats &&
    numberOfChats > 0 &&
    emailInput !== '' &&
    validateEmail(emailInput);

  const onChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setEmailInput(value);
    },
    []
  );

  const handleClose = () => {
    setEmailInput('');
    form.methods.reset();
    toggle();
  };

  const handleConfirm = () => {
    form.onSubmit();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      title={REMOVE_EMAIL_CONSTANTS.MODAL_HEADER}
      footerContent={{
        cancelText: 'Cancel',
        confirmText: getRemoveEmailButtonLabel(numberOfChats, emailInput),
        onCancel: handleClose,
        onConfirm: handleConfirm,
        isLoading: form.methods.formState.isSubmitting,
        isDisabled: form.isDisabled || !isReadyToSubmit,
        isDestructive: true,
      }}
    >
      <FormProvider {...form.methods}>
        <form onSubmit={form.onSubmit} data-testid="remove-email-form">
          <div className="space-y-4">
            <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
              <p className="text-warning-800 text-sm font-medium">
                ⚠️ {REMOVE_EMAIL_CONSTANTS.LABEL_EMAIL}
              </p>
            </div>
            <Input
              id="additionalEmail"
              name="additionalEmail"
              label="Email to remove"
              placeholder={REMOVE_EMAIL_CONSTANTS.PLACEHOLDER_EMAIL}
              value={emailInput}
              type="email"
              required
              isInvalid={!!form.methods.formState.errors.additionalEmail}
              errorMessage={
                form.methods.formState.errors.additionalEmail?.message
              }
              onChange={(value) => {
                form.methods.setValue('additionalEmail', value);
                onChange({
                  target: { value },
                } as ChangeEvent<HTMLInputElement>);
              }}
              className="w-full"
            />
          </div>
        </form>
      </FormProvider>
    </BaseModal>
  );
};
